import React, { useMemo } from "react";
import TrebbleSelect from "./TrebbleSelect";
import type { FolderOption } from "./MoveToFolderContent";

// Window.getI18n is declared globally elsewhere in the codebase — no need to
// redeclare. We reference it via `window.getI18n` with a runtime guard.

/* ----- Tree helpers ----- */
// Intentionally duplicated from MoveToFolderContent instead of extracted to
// a shared module — the tree layout there is tightly coupled to the modal's
// expand/collapse state, and this component only needs depth-aware flattening.
// Keeping the duplication localized is cheaper than a cross-file refactor.

interface TreeFolder extends FolderOption {
  children: TreeFolder[];
  depth: number;
}

function buildFolderTree(folders: FolderOption[]): TreeFolder[] {
  const map = new Map<string, TreeFolder>();
  const roots: TreeFolder[] = [];

  folders.forEach((folder) => {
    map.set(folder.id, { ...folder, children: [], depth: 0 });
  });

  folders.forEach((folder) => {
    const node = map.get(folder.id);
    if (!node) return;
    if (folder.parentFolderId && map.has(folder.parentFolderId)) {
      const parent = map.get(folder.parentFolderId);
      if (parent) {
        parent.children.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  const setDepths = (nodes: TreeFolder[], depth: number): void => {
    nodes.forEach((node) => {
      node.depth = depth;
      setDepths(node.children, depth + 1);
    });
  };
  setDepths(roots, 0);

  return roots;
}

function flattenTree(nodes: TreeFolder[]): TreeFolder[] {
  const result: TreeFolder[] = [];
  const walk = (items: TreeFolder[]): void => {
    items.forEach((node) => {
      result.push(node);
      walk(node.children);
    });
  };
  walk(nodes);
  return result;
}

/* ----- Component ----- */

const NO_FOLDER_VALUE = "";

export interface FolderPickerProps {
  folders: FolderOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  useEditorTheme?: boolean;
  ti18n?: unknown;
  disabled?: boolean;
  /** Optional id to attach to the underlying Select for testing / labels */
  id?: string;
}

const getI18n = (ti18n: unknown, key: string, fallback: string): string => {
  if (ti18n && window.getI18n) {
    return window.getI18n(ti18n, key);
  }
  return fallback;
};

/**
 * Reusable folder dropdown rendered as a TrebbleSelect (Ant Design combobox).
 *
 * - First option is "No folder" (value === "") which maps to `null` via onChange.
 * - Folder rows are indented by depth using paddingLeft and include a folder icon.
 * - Typeahead search uses the Ant Design `showSearch` feature.
 * - Dark mode and corner styling come from TrebbleSelect's theme tokens — no
 *   border-radius override is applied, consistent with the Trebble square-corner
 *   visual language.
 */
const FolderPicker: React.FC<FolderPickerProps> = ({
  folders,
  value,
  onChange,
  placeholder,
  useEditorTheme = false,
  ti18n,
  disabled,
  id,
}) => {
  const flatFolders = useMemo(() => flattenTree(buildFolderTree(folders)), [folders]);

  const resolvedPlaceholder = placeholder || getI18n(ti18n, "SELECT_FOLDER", "Select a folder");
  const noFolderLabel = getI18n(ti18n, "ROOT_NO_FOLDER", "No folder");

  const handleChange = (nextValue: string | undefined): void => {
    if (!nextValue) {
      onChange(null);
      return;
    }
    onChange(nextValue);
  };

  // `TrebbleSelect` expects `string | undefined`; mapping null -> undefined keeps
  // the combobox at its placeholder state when no folder is selected.
  const selectValue: string | undefined = value ?? undefined;

  return (
    <TrebbleSelect<string>
      id={id}
      value={selectValue}
      onChange={handleChange}
      placeholder={resolvedPlaceholder}
      showSearch
      allowClear
      optionFilterProp="label"
      useEditorTheme={useEditorTheme}
      disabled={disabled}
      style={{ width: "100%" }}
      options={[
        {
          value: NO_FOLDER_VALUE,
          label: noFolderLabel,
        },
        ...flatFolders.map((folder) => ({
          value: folder.id,
          label: folder.name,
        })),
      ]}
      optionRender={(option) => {
        const optionValue = option.value as string;
        const isNoFolder = optionValue === NO_FOLDER_VALUE;
        const folderNode = flatFolders.find((folder) => folder.id === optionValue);
        const depth = folderNode?.depth ?? 0;
        return (
          <div
            className="flex items-center gap-2"
            style={{ paddingLeft: isNoFolder ? 0 : depth * 16 }}
          >
            <span
              aria-hidden="true"
              className={`text-base shrink-0 leading-none ${isNoFolder ? "opacity-40" : ""}`}
            >
              <i className="pe-7s-folder" />
            </span>
            <span className={`truncate ${isNoFolder ? "text-gray-400" : ""}`}>
              {option.label}
            </span>
            {!isNoFolder && folderNode?.itemCount != null && (
              <span className="text-[11px] text-gray-400 shrink-0 ml-auto">
                {folderNode.itemCount}
              </span>
            )}
          </div>
        );
      }}
    />
  );
};

export default FolderPicker;
